import { addDays, addWeeks, subWeeks, subMonths, startOfWeek, endOfWeek, startOfDay, endOfDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { findAiEndDate } from "./dates"
import { addDbItem, getDbItem } from "./idb-util"
import { getObjectId } from "../comp/Utils"
import { isCatchAllAction } from "./sections"
import { TT_CACHE_MAX_AGE_MS, TT_CACHE_SYNC_SKEW_MS, getTtCacheSyncStamp, isTtCacheFresh } from "./tt-cache"

/**
 * The task list behind the timesheet entry panel ("My activities" / "Project
 * specific activities").
 *
 * Data: one IndexedDB record per project holding every task the panel may ever
 * show, rendered the moment it is read and kept in step with the server by a
 * delta fetch (`updatedAt gte` the last sync) on the indexed find path - never
 * a filtered/search query. Grouping and the show/drop rules are pure arithmetic
 * over that array, done on the client against the week being filled in.
 *
 * Record shape (keyed by mission id):
 *   { id, v, syncedAt, items: [task] }
 * Missing, wrong version or too old = cache miss = full fetch.
 */

export const TT_TASKS_DB = "timeTrackingProjectTasks"

// Bump when TT_TASK_SELECT or the stored shape changes.
export const TT_TASKS_CACHE_VERSION = 4

// The server caps this route at 100 per page.
export const TT_TASKS_PAGE_LIMIT = 100

export const TT_TASK_SELECT = [
    "_id",
    "_archived",
    "title",
    "actionId",
    "people",
    "metaType",
    "status",
    "completedOn",
    "week",
    "startDate",
    "endDate",
    "startHour",
    "estimatedDuration",
    "y",
    "updatedAt",
    "+checklist",
]

// Display rules, relative to the week being filled in.
export const DONE_SHOW_WEEKS = 3 // done earlier than this many weeks before the week: not shown
export const ABANDONED_SHOW_MONTHS = 3 // open and ended earlier than this: abandoned, not shown
export const FUTURE_SHOW_WEEKS = 2 // open and starting later than this after the week: not shown

// Cache rules, relative to today. Wider than the display rules so that filling
// in a past week still finds what was live then; the display rules do the rest.
export const DONE_CACHE_MONTHS = 3
export const ABANDONED_CACHE_MONTHS = 6

// A task write the app saw for a project (its own or over the socket) marks
// the project touched; a cache synced before that moment is not fresh. Kept in
// localStorage so another tab of the same person sees it too.
const TOUCHED_KEY = "ttTasksTouched:"

// A touch this close before a sync may have landed after the delta ran.
export const TOUCH_MARGIN_MS = 30 * 1000

export function touchProjectTasks(missionId, now = Date.now()) {
    if (!missionId) {
        return
    }

    try {
        window.localStorage.setItem(TOUCHED_KEY + missionId, String(now))
    } catch (e) {}
}

export function getProjectTasksTouchedAt(missionId) {
    try {
        const value = Number(window.localStorage.getItem(TOUCHED_KEY + missionId))

        return Number.isFinite(value) && value > 0 ? value : null
    } catch (e) {
        return null
    }
}

/**
 * Fresh enough to skip the server: synced recently and nothing on the project
 * was written since that sync.
 */
export function isTaskCacheFresh(cache, missionId, now = Date.now()) {
    if (!isTtCacheFresh(cache, now)) {
        return false
    }

    const touchedAt = getProjectTasksTouchedAt(missionId)

    if (!touchedAt) {
        return true
    }

    const syncedWallClock = new Date(cache.syncedAt).getTime() + TT_CACHE_SYNC_SKEW_MS

    return touchedAt < syncedWallClock - TOUCH_MARGIN_MS
}

export const GROUPS = [
    { key: "thisWeek", title: "This week" },
    { key: "lastWeek", title: "Last week" },
    { key: "older", title: "Older" },
    { key: "unscheduled", title: "Unscheduled" },
]

const WEEK_OPTS = { weekStartsOn: 1 }

export function getWeekBounds(date) {
    return { start: startOfWeek(date, WEEK_OPTS), end: endOfWeek(date, WEEK_OPTS) }
}

function toDate(value) {
    if (value === null || value === undefined || value === "") {
        return null
    }

    const d = toZonedTime(value)

    return d && !Number.isNaN(d.getTime()) ? d : null
}

export function isTaskDone(task) {
    return task?.status === "done" || task?.done === true
}

/**
 * Where a task starts. Agile tasks carry a week (plus the hour inside it the
 * board placed them at); gantt tasks carry a start date.
 */
export function findAiStartDate(task) {
    const week = toDate(task?.week)

    if (week) {
        const hour = Number(task.startHour)

        return startOfDay(addDays(week, Number.isFinite(hour) && hour > 0 ? Math.floor(hour / 8) : 0))
    }

    const start = toDate(task?.startDate)

    return start ? startOfDay(start) : null
}

/**
 * The dates the rules work with. `doneOn` is the completion date, or failing
 * that the task's end date (completedOn used to be stamped by the web client
 * only). A missing end falls back to the start day, and vice versa.
 */
export function getTaskDates(task) {
    const done = isTaskDone(task)
    let start = findAiStartDate(task)
    let end = null

    try {
        end = findAiEndDate(task) || null
    } catch (e) {
        end = null
    }

    if (end && Number.isNaN(end.getTime())) {
        end = null
    }

    if (!end && start) {
        end = endOfDay(start)
    }

    if (!start && end) {
        start = startOfDay(end)
    }

    const completedOn = toDate(task?.completedOn)
    const doneOn = done ? completedOn || end : null

    return { done, start, end, doneOn }
}

function overlaps(start, end, bounds) {
    return start <= bounds.end && end >= bounds.start
}

/**
 * Which group a task belongs to for the week `anchor` falls in, and its badge
 * flags. Returns null when the task is not shown at all.
 *
 * Done:   by completion date. This week / last week / up to DONE_SHOW_WEEKS
 *         back = older / earlier = dropped. No date at all = unscheduled.
 * Open:   by its scheduled range. Overlaps the week = this week, overlaps the
 *         week before = last week, ended earlier = older. Starts after the week
 *         but within FUTURE_SHOW_WEEKS = this week (it can be picked up early),
 *         later = dropped. Ended more than ABANDONED_SHOW_MONTHS ago = dropped.
 *         No dates = unscheduled.
 */
export function classifyTask(task, { anchor, now = new Date() } = {}) {
    const week = getWeekBounds(anchor || now)
    const prevWeek = getWeekBounds(subWeeks(week.start, 1))
    const { done, start, end, doneOn } = getTaskDates(task)

    if (done) {
        if (!doneOn) {
            return { group: "unscheduled", isDone: true, isOverdue: false }
        }

        if (doneOn < subWeeks(week.start, DONE_SHOW_WEEKS)) {
            return null
        }

        const group = doneOn >= week.start ? "thisWeek" : doneOn >= prevWeek.start ? "lastWeek" : "older"

        return { group, isDone: true, isOverdue: false }
    }

    if (!start || !end) {
        return { group: "unscheduled", isDone: false, isOverdue: false }
    }

    if (start > addWeeks(week.end, FUTURE_SHOW_WEEKS)) {
        return null
    }

    if (end < subMonths(week.start, ABANDONED_SHOW_MONTHS)) {
        return null
    }

    const isOverdue = end < now

    let group

    if (start > week.end) {
        group = "thisWeek"
    } else if (overlaps(start, end, week)) {
        group = "thisWeek"
    } else if (overlaps(start, end, prevWeek)) {
        group = "lastWeek"
    } else {
        group = "older"
    }

    return { group, isDone: false, isOverdue }
}

/**
 * The rows the panel can offer: parent tasks plus their sub-tasks as separate
 * entries (a sub-task without dates of its own follows its parent's group).
 * Titleless, placeholder and hidden tasks are left out as before.
 */
export function expandTasks(items) {
    const rows = []

    ;(items || []).forEach((task) => {
        if (!task || task._archived || !task.title || task.title === "Activity..." || task.metaType === 4) {
            return
        }

        rows.push({ task, parent: null })
        ;(task.checklist || []).forEach((item) => {
            if (!item || !item.title) {
                return
            }

            rows.push({ task: { ...item, parentActionItemId: task._id }, parent: task })
        })
    })

    return rows
}

const SCHEDULE_FIELDS = ["week", "startHour", "startDate", "endDate", "dueDate", "estimatedDuration"]

/**
 * Sub-task dates are loose until the work is done, so a sub-task is scheduled
 * by its parent: the parent's dates, its own done state and completion date
 * (a done one without completedOn falls back to the parent's end).
 */
function withParentSchedule(task, parent) {
    const scheduled = { ...task }

    SCHEDULE_FIELDS.forEach((field) => {
        scheduled[field] = parent[field]
    })

    return scheduled
}

/**
 * Sub-tasks are only offered once they are under way: done, or in the second
 * ("in progress") column of the task screen's sub-task layout.
 */
function isSubTaskUnderWay(task) {
    return isTaskDone(task) || task.col === "two"
}

function isMine(task, personId) {
    if (!personId) {
        return false
    }

    if (Array.isArray(task.people) && task.people.some((p) => getObjectId(p) === personId)) {
        return true
    }

    return !!task.person && getObjectId(task.person) === personId
}

function compareRanked(a, b) {
    if (a.isOverdue !== b.isOverdue) {
        return a.isOverdue ? -1 : 1
    }

    const ea = a.end ? a.end.getTime() : Infinity
    const eb = b.end ? b.end.getTime() : Infinity

    if (ea !== eb) {
        return ea - eb
    }

    // Higher up the board first
    const ya = Number.isFinite(a.rankY) ? a.rankY : Infinity
    const yb = Number.isFinite(b.rankY) ? b.rankY : Infinity

    if (ya !== yb) {
        return ya - yb
    }

    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
}

function compareTitle(a, b) {
    return (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: "base" })
}

function hasNoOneAssigned(task) {
    return !task.person && !(Array.isArray(task.people) && task.people.length)
}

/**
 * The search box: a row matches on its title, a sub-task also on its parent
 * task's title (the line under it).
 */
export function filterTaskRows(rows, text) {
    const needle = (text || "").toLowerCase()
    const matches = (value) => (value || "").toLowerCase().includes(needle)

    if (!needle) {
        return rows
    }

    return rows.filter((r) => matches(r.title) || (r.isSubTask && matches(r.parentTitle)))
}

/**
 * Splits the offered rows into the person's own tasks, grouped and ordered,
 * and everyone else's, flat and alphabetical. Both lists apply the drop rules;
 * only "mine" is shown grouped. Inside a group: overdue first, then end date,
 * then board position (lower y = higher up), then title. `skip(task)` lets the caller leave out tasks
 * already on the timesheet.
 *
 * Sub-tasks are rows of their own (`isSubTask`, `parentTitle`); one with no
 * one assigned belongs to the parent task's people.
 */
export function rankTasks(items, { anchor, now = new Date(), personId, skip } = {}) {
    const mine = []
    const notMine = []

    expandTasks(items).forEach(({ task, parent }) => {
        if ((skip && skip(task)) || (parent && !isSubTaskUnderWay(task))) {
            return
        }

        const scheduled = parent ? withParentSchedule(task, parent) : task
        const rank = classifyTask(scheduled, { anchor, now })

        if (!rank) {
            return
        }

        const { end } = getTaskDates(scheduled)
        const row = {
            ...task,
            group: rank.group,
            isDone: rank.isDone,
            isOverdue: rank.isOverdue,
            end,
            rankY: parent ? parent.y : task.y,
            isSubTask: parent ? true : undefined,
            parentTitle: parent ? parent.title : undefined,
        }

        if (isMine(task, personId) || (parent && hasNoOneAssigned(task) && isMine(parent, personId))) {
            mine.push(row)
        } else {
            notMine.push(row)
        }
    })

    const order = new Map(GROUPS.map((g, i) => [g.key, i]))

    mine.sort((a, b) => order.get(a.group) - order.get(b.group) || compareRanked(a, b))
    notMine.sort(compareTitle)

    return { mine, notMine }
}

/**
 * Task rows that share a name with another one in the lists (sub-tasks aside:
 * they name their parent task instead, never a package).
 */
function duplicateTitleGroups(lists) {
    const byTitle = new Map()

    lists.forEach((rows) =>
        (rows || []).forEach((r) => {
            if (r.isSubTask || !r.title) {
                return
            }

            const key = r.title.trim().toLowerCase()

            byTitle.set(key, [...(byTitle.get(key) || []), r])
        }),
    )

    return Array.from(byTitle.values()).filter((rows) => rows.length > 1)
}

export function hasDuplicateTitles(lists) {
    return duplicateTitleGroups(lists).length > 0
}

// Same-name tasks are told apart by their action: the package on a gantt, the section on a
// board (a board section is an action now; the board's catch-all action has no label)
function distinguishingField(rows) {
    const distinct = (field) => new Set(rows.map((r) => getObjectId(r[field]) || "")).size > 1

    return distinct("actionId") ? "actionId" : null
}

/**
 * What labelling the duplicates needs: the action ids of same-name tasks in
 * different packages / sections.
 */
export function packageTitlesNeeded(lists) {
    const actionIds = new Set()

    duplicateTitleGroups(lists).forEach((rows) => {
        if (distinguishingField(rows) === "actionId") {
            rows.forEach((r) => getObjectId(r.actionId) && actionIds.add(getObjectId(r.actionId)))
        }
    })

    return { actionIds: Array.from(actionIds) }
}

/**
 * Tasks with the same name get `packageLabel`: the title of their package or section when
 * those differ (none for a task in the board's catch-all). Everything else is returned as
 * it was.
 *
 * `titles` = { actions: Map(id -> title) }
 */
export function labelDuplicateTitles(lists, titles) {
    const labels = new Map()

    duplicateTitleGroups(lists).forEach((rows) => {
        const field = distinguishingField(rows)
        const names = field === "actionId" ? titles?.actions : null

        if (!names) {
            return
        }

        rows.forEach((r) => {
            const label = names.get(getObjectId(r[field]))

            if (label) {
                labels.set(r, label)
            }
        })
    })

    return lists.map((rows) => (rows || []).map((r) => (labels.has(r) ? { ...r, packageLabel: labels.get(r) } : r)))
}

// Per project for the session: action id -> Promise<title|null>
const packageTitles = new Map()

/**
 * Titles for the given package / section ids. Only ids not asked for yet this session are
 * fetched, in one request by id; a failed fetch is forgotten so the next open tries again.
 * The board's catch-all action gets no title.
 *
 * `fetchActions(ids)` resolves to [{ _id, title, source, aiSource, isSection }].
 */
export async function loadPackageTitles(missionId, { actionIds = [] }, fetchers) {
    if (!missionId) {
        return null
    }

    if (!packageTitles.has(missionId)) {
        packageTitles.set(missionId, { actions: new Map() })
    }

    const entry = packageTitles.get(missionId)
    const missing = actionIds.filter((id) => !entry.actions.has(id))

    if (missing.length) {
        const batch = fetchers
            .fetchActions(missing)
            .then(
                (list) =>
                    new Map((list || []).map((a) => [getObjectId(a._id), isCatchAllAction(a) ? null : a.title])),
            )

        missing.forEach((id) => {
            entry.actions.set(
                id,
                batch.then(
                    (found) => found.get(id) ?? null,
                    () => {
                        entry.actions.delete(id)
                        return null
                    },
                ),
            )
        })
    }

    const titles = await Promise.all(actionIds.map((id) => entry.actions.get(id)))

    const actions = new Map()

    actionIds.forEach((id, i) => {
        if (titles[i]) {
            actions.set(id, titles[i])
        }
    })

    return { actions }
}

/**
 * What stays in the device cache: archived tasks go, and so do tasks no week
 * the person can still fill in would show.
 */
export function pruneForCache(items, now = new Date()) {
    const doneCutoff = subMonths(now, DONE_CACHE_MONTHS)
    const abandonedCutoff = subMonths(now, ABANDONED_CACHE_MONTHS)

    return (items || []).filter((task) => {
        if (!task || task._archived) {
            return false
        }

        const { done, end, doneOn } = getTaskDates(task)

        if (done) {
            return !doneOn || doneOn >= doneCutoff
        }

        return !end || end >= abandonedCutoff
    })
}

/**
 * Fresh rows replace cached ones by id; archived rows are removed.
 */
export function mergeTaskDelta(cached, fetched) {
    const byId = new Map()

    ;(cached || []).forEach((task) => {
        const id = getObjectId(task?._id)

        if (id) {
            byId.set(id, task)
        }
    })
    ;(fetched || []).forEach((task) => {
        const id = getObjectId(task?._id)

        if (!id) {
            return
        }

        if (task._archived) {
            byId.delete(id)
        } else {
            byId.set(id, task)
        }
    })

    return Array.from(byId.values())
}

export async function readTaskCache(missionId, now = Date.now()) {
    if (!missionId) {
        return null
    }

    try {
        const record = await getDbItem(TT_TASKS_DB, "store", missionId)

        if (!record || record.v !== TT_TASKS_CACHE_VERSION || !record.syncedAt || !Array.isArray(record.items)) {
            return null
        }

        const syncedAtMs = new Date(record.syncedAt).getTime()

        if (!Number.isFinite(syncedAtMs) || now - syncedAtMs > TT_CACHE_MAX_AGE_MS) {
            return null
        }

        return { syncedAt: record.syncedAt, items: record.items }
    } catch (err) {
        console.warn("tt-task-groups: read failed", err)
        return null
    }
}

export async function writeTaskCache(missionId, { syncedAt, items }) {
    if (!missionId || !syncedAt || !Array.isArray(items)) {
        return
    }

    try {
        await addDbItem(TT_TASKS_DB, "store", { id: missionId, v: TT_TASKS_CACHE_VERSION, syncedAt, items })
    } catch (err) {
        console.warn("tt-task-groups: write failed", err)
    }
}

/**
 * The project's tasks: what the cache holds is handed to `onCached` straight
 * away so the panel can render. A cache synced in the last few minutes with no
 * task write on the project since is the answer; otherwise one delta fetch
 * brings it up to date (a full fetch when there is no usable cache).
 *
 * `fetchTasks(params)` resolves to the full list for the given query params and
 * is injected so this stays free of the API layer.
 */
export async function loadProjectTasks({ missionId, fetchTasks, onCached, now = Date.now() }) {
    const cache = await readTaskCache(missionId, now)

    if (cache && onCached) {
        onCached(cache.items)
    }

    if (cache && isTaskCacheFresh(cache, missionId, now)) {
        return cache.items
    }

    const syncedAt = getTtCacheSyncStamp(now)
    const params = { limit: TT_TASKS_PAGE_LIMIT, select: TT_TASK_SELECT }

    if (cache) {
        params.updatedAt = `gte ${cache.syncedAt}`
        params.withArchived = true
    }

    let fetched

    try {
        fetched = await fetchTasks(params)
    } catch (err) {
        // Offline or a server hiccup: whatever is cached is still worth showing.
        if (cache) {
            return cache.items
        }

        throw err
    }

    // Nothing changed on the server: the same array goes back, so the caller
    // can tell there is nothing to re-render.
    const items =
        cache && !fetched?.length
            ? cache.items
            : pruneForCache(mergeTaskDelta(cache ? cache.items : [], fetched || []), new Date(now))

    await writeTaskCache(missionId, { syncedAt, items })

    return items
}
